<template>
  <div
    class="textarea-holder"
    :class="[hasColumnsSet, { disabled: isDisabled }, { 'is-active': active }]"
    v-b-tooltip="tooltip"
  >
    <label v-if="label" :class="['rbz-label', { disabled: isDisabled }]">
      {{ label }}
      <slot name="postLabel" />
    </label>
    <textarea
      :cols="cols || 20"
      :rows="rows || 2"
      :data-qa="dataQa"
      :value="inputValue"
      :class="[
        'form-textarea',
        'rbz-text-regular',
        classes,
        errorMessage?.class,
        { filled: inputValue },
      ]"
      :style="`resize: ${resize}`"
      :placeholder="placeholder"
      :name="name"
      :disabled="isDisabled"
      autocomplete="off"
      @focus="textareaFocus($event)"
      @blur="textareaBlur($event)"
      @input="emit('update:inputValue', ($event.target as HTMLInputElement).value)"
    />
    <span v-if="errorMessage?.message" class="error-message rbz-text-small">
      {{ errorMessage.message }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

interface FormTextareaProps {
  name: string;
  label?: string;
  resize?: string;
  classes?: string;
  placeholder?: string;
  inputValue: string;
  required?: boolean;
  disabled?: boolean;
  isUpdateInProcess?: boolean;
  ignoreUpdateInProcess?: boolean;
  cols?: string | number;
  rows?: string | number;
  isInvalid?: boolean;
  errorMessageText?: string;
  tooltip?: string;
  dataQa?: string;
}

const props = withDefaults(defineProps<FormTextareaProps>(), {
  label: '',
  classes: '',
  placeholder: '',
  required: false,
  disabled: false,
  cols: null,
  rows: null,
  errorMessageText: '',
  resize: 'vertical',
  tooltip: '',
  dataQa: '',
});

interface FormTextareaEmits {
  (e: 'update:inputValue', value: string | number): void;

  (e: 'focus', value: Event): void;

  (e: 'blur', value: Event): void;
}

const emit = defineEmits<FormTextareaEmits>();

const isDisabled = computed(() => {
  return props.disabled || (props.isUpdateInProcess && !props.ignoreUpdateInProcess);
});

const isDirty = ref(false);
watch(
  () => props.inputValue,
  async () => {
    isDirty.value = true;
  }
);

const errorMessage = computed(() => {
  if (props.isInvalid && isDirty.value) {
    return {
      class: 'error',
      message: props.errorMessageText,
    };
  }

  return {
    class: '',
    message: '',
  };
});

const active = ref(false);
const textareaFocus = (event: Event) => {
  active.value = true;
  emit('focus', event);
};
const textareaBlur = (event: Event) => {
  active.value = false;
  emit('blur', event);
};

const hasColumnsSet = computed(() => {
  if (!props.cols) {
    return 'full-width';
  }

  return '';
});

const setIsDirty = (value: boolean) => {
  isDirty.value = value;
};

defineExpose({
  setIsDirty,
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/colors' as colors;

.textarea-holder {
  display: flex;
  flex-direction: column;
  position: relative;

  .full-width {
    width: 100%;
  }

  .form-textarea {
    background: map.get(colors.$colors, 'white');
    border: 1px solid map.get(colors.$colors, 'silver');
    border-radius: 4px;
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
    box-sizing: border-box;
    color: map.get(colors.$colors, 'shuttle-gray');
    padding: 10px 12px;
    width: 100%;
  }

  .form-textarea::placeholder {
    color: map.get(colors.$colors, 'dusty-gray');
  }

  .form-textarea:hover {
    background: map.get(colors.$colors, 'selago');
    border: 1px solid map.get(colors.$colors, 'silver');
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
    color: map.get(colors.$colors, 'shuttle-gray');
  }

  &.is-active {
    .form-textarea,
    .form-textarea:focus {
      border: 1px solid map.get(colors.$colors, 'jewel');
      box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
      outline: none;
    }
  }

  .error {
    background: map.get(colors.$colors, 'white');
    border: 1px solid map.get(colors.$colors, 'cinnabar');
    border-radius: 4px;
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
  }

  .error-message {
    color: map.get(colors.$colors, 'flamingo');
    margin-top: 4px;
  }
}

.textarea-holder.disabled {
  .form-textarea {
    background: map.get(colors.$colors, 'white');
    border: 1px solid map.get(colors.$colors, 'alto');
    border-radius: 4px;
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
    color: map.get(colors.$colors, 'silver');
    cursor: text;
  }

  .form-textarea::placeholder {
    color: map.get(colors.$colors, 'silver');
  }

  .form-textarea:hover {
    background: map.get(colors.$colors, 'white');
  }
}
</style>
